import type { AxiosError } from "axios";
import type {
  AddCompanyPayload,
  CompanyDatabase,
  CompanyDatabaseRow,
} from "~/app/sales/accounts/types/common.type";

import { axios, getBackendApiErrorResponse } from "~/core/axios";
import type {
  BackendApiErrorResponse,
  BackendApiResponse,
} from "~/core/types/backend-api-response.type";
import type {
  AttributeType,
  PaginationOptions,
} from "~/core/types/common.type";

import type { GetOrgObjectWithFilteringBody } from "./types";

export type GetOrgCompanyRow = {
  id: string;
  company_name: string;
  company_domain: string;
  [property_name: string]: string | number | null;
};

export type GetOrgCompanyResponse = {
  rowData: Array<GetOrgCompanyRow>;
  hiddenAttributes: Array<{
    id: string;
    property_name: string;
    current_name: string;
    attribute_order: number;
    attribute_type: AttributeType;
  }>;
};

export const getCompanyDataByOrgId = async (
  query: GetOrgObjectWithFilteringBody,
) => {
  const blankRow: CompanyDatabaseRow = {
    id: "",
    company_domain: "",
    company_name: "",
  };
  const blankPagination: PaginationOptions = {
    limit: 20,
    page: 0,
    total: 0,
  };

  try {
    const { q, limit, page, filters, sortings } = query;
    const searchParams = new URLSearchParams();

    if (q) {
      searchParams.append("q", q);
    }
    if (limit) {
      searchParams.append("limit", limit.toString());
    }
    if (page) {
      searchParams.append("page", page.toString());
    }

    if (filters?.length && filters?.length > 0) {
      searchParams.append("filters", JSON.stringify(filters));
    }

    if (sortings?.length && sortings?.length > 0) {
      searchParams.append("sortings", JSON.stringify(sortings));
    }

    const { data } = await axios.get<
      BackendApiResponse<
        GetOrgCompanyResponse & { metadata: PaginationOptions }
      >
    >(`/org/company?${searchParams.toString()}`);

    if (!data.data) {
      return { rows: [blankRow], pagination: blankPagination };
    }

    const rows: CompanyDatabase["rows"] = data.data?.rowData || [];

    return { rows, pagination: data.data.metadata };
  } catch (error) {
    const err = error as AxiosError<BackendApiResponse>;
    console.error(err.response);
    return { rows: [blankRow], pagination: blankPagination };
  }
};

export const createCompany = async (data: AddCompanyPayload) => {
  return axios.post<BackendApiResponse<CompanyDatabaseRow>>(
    "/org/company",
    data,
  );
};

export const deleteCompanyById = async (id: string, accessToken: string) => {
  return axios.delete<BackendApiResponse>(`/org/company/${id}`);
};

export const getClientCompanyDBDataById = async (id: string) => {
  "use client";
  try {
    const { data } = await axios.get<BackendApiResponse<CompanyDatabaseRow>>(
      `/accounts/${id}`,
    );

    return data.data;
  } catch (error) {
    const err = error as AxiosError<BackendApiErrorResponse>;
    console.error(err.response);
    return null;
  }
};

export const updateAccountDataByPropertyName = async (body: {
  object_id: string;
  property_name: string;
  value: any;
}) => {
  try {
    await axios.patch<BackendApiResponse>(`/accounts/${body.object_id}`, [
      { property_name: body.property_name, value: body.value },
    ]);
  } catch (error) {
    const err = getBackendApiErrorResponse(error);
    throw new Error(err.data.message || "Failed to update account data");
  }
};
